import {useMemo, useState} from 'react';
import {Dialog} from '@mui/material';
import PracticeHint from './PracticeHint';
import ReceiptCreationFlow from '../ReceiptFlow/ReceiptCreationFlow';
import SplitSummary from '../ReceiptFlow/SplitSummary';
import {draftRows} from '../ReceiptFlow/ReceiptReview';
import QuantityAssignment from '../ReceiptFlow/QuantityAssignment';
import {lineTotalCents, ReviewRow} from '../ReceiptFlow/reviewModel';
import {allocatePortions} from '../ReceiptFlow/portionModel';
import {Receipt, ReceiptPortion, ScanReceiptResponse} from '../../../services/receipt/receiptModel';
import {participantKey, participantName, SplitParticipant} from '../../../services/groups/participants';
import classes from './SplitPractice.module.scss';

const people: SplitParticipant[] = [{id: 1, username: 'Alex', email: ''},
  {id: 2, username: 'Maria', email: ''}, {id: 3, name: 'Sam'}];
const all = people.map(participantKey);
const initialRows: ReviewRow[] = [
  {id: 1, name: 'Coffee', quantity: 1, price: 3, memberIds: []},
  {id: 2, name: 'Pad Thai', quantity: 2, price: 12.5, memberIds: []},
  {id: 3, name: 'Spring Rolls', quantity: 1, price: 7.5, memberIds: []},
  {id: 4, name: 'Drinks', quantity: 3, price: 3.5, memberIds: []},
];
const lessons: {rowId: number; title: string; content: string; button: string; portion: ReceiptPortion}[] = [
  {rowId: 1, title: 'One item, one person', content: 'Alex had the coffee. Tap Alex on the Coffee card.',
    button: 'Assign one Coffee to Alex', portion: {quantity: 1, participantKeys: ['user:1']}},
  {rowId: 2, title: 'Assign one of two', content: 'There are two Pad Thai meals. Give the first one to Alex.',
    button: 'Assign one Pad Thai to Alex', portion: {quantity: 1, participantKeys: ['user:1']}},
  {rowId: 2, title: 'The other meal is Maria’s', content: 'Tap Maria for the remaining Pad Thai. Each person pays for one meal.',
    button: 'Assign one Pad Thai to Maria', portion: {quantity: 1, participantKeys: ['user:2']}},
  {rowId: 3, title: 'Share a starter', content: 'Everyone shared the Spring Rolls. Tap All to divide that item equally.',
    button: 'Share remaining Spring Rolls with everyone', portion: {quantity: 1, participantKeys: all}},
  {rowId: 4, title: 'Share only part of a quantity', content: 'Open Custom for Drinks. Set quantity to 2, select Alex, Maria and Sam, then confirm.',
    button: 'Custom split for Drinks', portion: {quantity: 2, participantKeys: all}},
  {rowId: 4, title: 'Try custom percentages', content: 'Open Custom for the last drink. Select Maria and Sam. Choose Adjust shares, enter Maria 60% and Sam 40%, then confirm.',
    button: 'Custom split for Drinks', portion: {quantity: 1, participantKeys: ['user:2', 'guest:3'], basisPoints: [6000, 4000]}},
];

function signature(portion: ReceiptPortion) {
  return JSON.stringify([portion.quantity, portion.participantKeys.map((key, index) =>
    [key, portion.basisPoints?.[index] ?? null]).sort(([first], [second]) => String(first).localeCompare(String(second)))]);
}

export default function SplitPractice({onExit, onScan, onPlans}: {onExit: () => void; onScan: () => void; onPlans: () => void}) {
  const [rows, setRows] = useState(initialRows);
  const [captured, setCaptured] = useState(false);
  const [summary, setSummary] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const fixture = useMemo(() => {
    const merchant = ['Thai Garden', 'Little Bangkok', 'Jasmine Kitchen'][Math.floor(Math.random() * 3)];
    const scan: ScanReceiptResponse = {scanId: 'practice-only', merchantName: merchant, receiptDate: null,
      products: initialRows, currency: 'EUR', total: 46, needsReview: true, warnings: [],
      quota: {unlimited: true, limit: null, remaining: null, used: 0, resetAt: '2099-01-01T00:00:00Z'}};
    const image = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="420" height="460"><rect width="420" height="460" fill="#fffdf6"/>' +
      '<g font-family="monospace" font-size="18" fill="#222"><text x="30" y="50">' + merchant + '</text>' +
      '<text x="30" y="90">FICTIONAL PRACTICE RECEIPT</text>' +
      initialRows.map((row, index) => '<text x="30" y="' + (150 + index * 48) + '">' + row.quantity + ' x ' + row.name + '   EUR ' + (row.quantity * row.price).toFixed(2) + '</text>').join('') +
      '<text x="30" y="390">TOTAL EUR 46.00</text></g></svg>');
    return {scan, image, groups: [{id: 1, name: 'Dinner friends', users: [
      {id: 1, username: 'Alex', email: ''}, {id: 2, username: 'Maria', email: ''}], guests: [{id: 3, name: 'Sam'}]}]};
  }, []);
  const scripted = rows.length === initialRows.length && rows.every((row, index) =>
    row.name === initialRows[index].name && row.quantity === initialRows[index].quantity);
  const lessonIndex = lessons.findIndex(lesson => {
    const rowIndex = rows.findIndex(row => row.id === lesson.rowId);
    const row = rows[rowIndex];
    if (!row || allocatePortions(row, rowIndex, all).complete) return false;
    return !row.allocations?.some(portion => signature(portion) === signature(lesson.portion));
  });
  const lesson = scripted ? lessons[lessonIndex] : undefined;
  const remainingDrinks = allocatePortions(rows.find(row => row.id === 4) ?? {quantity: 0, price: 0}, 3, all).remaining;
  const lessonContent = lesson?.rowId === 4 && remainingDrinks < 2 && lessonIndex === 4
    ? 'Open Custom for the remaining drink. Select the people sharing it, then confirm.' : lesson?.content;
  const complete = rows.every((row, index) => allocatePortions(row, index, all).complete);
  const restart = () => {setRows(initialRows); setCaptured(false); setSummary(false); setCustomOpen(false); setAttempt(value => value + 1);};
  const hint = <div className={classes.hint} role="status">
    <strong>{complete ? 'All assigned — review the totals below' : lesson ? `${lessonIndex + 1} of ${lessons.length} · ${lesson.title}` : 'Try your edited receipt'}</strong>
    <p>{complete ? 'Trash icons undo assignments. Finish practice to see the summary; nothing will be saved.' : lessonContent ?? 'Assign your edited items to any of the sample participants, then finish practice.'}</p>
  </div>;
  return <Dialog open fullScreen disableEnforceFocus onClose={onExit} aria-label="Practice splitting a receipt">
    <div className={classes.practice}>
      <div className={classes.bar}><span>Practice receipt · No scans used · Nothing saved</span><button onClick={onExit}>Exit practice</button></div>
      {!captured ? <ReceiptCreationFlow practice={{...fixture,
        onBack: onExit,
        onSplit: draft => {
          setRows(draftRows(draft).map((row, index) => ({...row, id: index + 1})));
          setCaptured(true);
        }}}/> : summary ? <SplitSummary practice receipt={{
          name: fixture.scan.merchantName ?? 'Practice dinner', groupName: 'Dinner friends', currency: 'EUR',
          totalPrice: rows.reduce((sum, row) => sum + lineTotalCents(row), 0) / 100,
          entries: people.map(person => ({participantKey: participantKey(person), participantName: participantName(person),
            products: [], totalPrice: rows.reduce((sum, row, index) => sum + (allocatePortions(row, index, all).perParticipant[participantKey(person)] ?? 0), 0) / 100})),
        } as Receipt} onDone={onScan} onOpen={onPlans}/> : <>
        <QuantityAssignment key={attempt} rows={rows} participants={people} currency="EUR"
          onChange={(rowId, allocations) => {
            setRows(previous => previous.map(row => row.id === rowId ? {...row, allocations} : row));
          }}
          onSave={() => {if (complete) setSummary(true);}} canSave={complete} saving={false} saveLabel="Finish practice"
          onCustomOpenChange={setCustomOpen} customHelp={<>{hint}
            {customOpen && lesson && <PracticeHint key={`custom-${lessonIndex}`} target="#shared-quantity"
              title="Choose the quantity and people" content={lessonContent ?? lesson.content}/>}
          </>}
          editingName={<>{hint}<button className={classes.restart} onClick={restart}>Restart practice</button></>}
          guests={<p>Sam is a guest. On real receipts, you can add someone without an account.</p>}
          errors={null}/>
        {lesson && <PracticeHint key={`${attempt}:${lessonIndex}`} paused={customOpen}
          target={`[aria-label="${lesson.button}"]`} title={lesson.title} content={lessonContent ?? lesson.content}/>}
      </>}
    </div>
  </Dialog>;
}
